
import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import MainLayout from "./components/Layout/MainLayout";
import AppLayout from "./components/Layout/AppLayout";
import NotFound from "./pages/NotFound";

// Public pages
const Home = lazy(() => import("./pages/Home"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Chat = lazy(() => import("./pages/Chat"));
const Riskbedomning = lazy(() => import("./pages/Riskbedomning"));

// App pages (authenticated)
const AppDashboard = lazy(() => import("./pages/app/AppDashboard"));
const AppProjects = lazy(() => import("./pages/app/AppProjects"));
const AppProject = lazy(() => import("./pages/app/AppProject"));
const AppNotifications = lazy(() => import("./pages/app/AppNotifications"));
const AppProfile = lazy(() => import("./pages/app/AppProfile"));

// Lazy admin
const Admin = lazy(() => import("./pages/Admin"));

// Home redirect component - redirects authenticated users to /app/dashboard
import { useAuth } from "@/context/auth";
import { Suspense } from "react";

function HomeOrRedirect() {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/app/dashboard" replace />;
  }
  
  return (
    <MainLayout>
      <Suspense fallback={<div>Laddar...</div>}>
        <Home />
      </Suspense>
    </MainLayout>
  );
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: "/",
    element: <HomeOrRedirect />,
  },
  {
    path: "/auth",
    element: <Suspense fallback={<div>Laddar...</div>}><Auth /></Suspense>,
  },
  {
    path: "/reset-password",
    element: <MainLayout><Suspense fallback={<div>Laddar...</div>}><ResetPassword /></Suspense></MainLayout>,
  },
  {
    path: "/riskbedomning",
    element: <MainLayout><Suspense fallback={<div>Laddar...</div>}><Riskbedomning /></Suspense></MainLayout>,
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/chat",
    element: <MainLayout><Suspense fallback={<div>Laddar...</div>}><Chat /></Suspense></MainLayout>,
  },

  // App routes (authenticated, with sidebar)
  {
    path: "/app",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/app/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <AppDashboard />,
      },
      {
        path: "projects",
        element: <AppProjects />,
      },
      {
        path: "project/:projectId",
        element: <AppProject />,
      },
      {
        path: "notifications",
        element: <AppNotifications />,
      },
      {
        path: "profile",
        element: <AppProfile />,
      },
      {
        path: "admin",
        element: <Suspense fallback={<div>Laddar...</div>}><Admin /></Suspense>,
      },
    ],
  },

  // Legacy redirects
  {
    path: "/uppdrag",
    element: <Navigate to="/app/projects" replace />,
  },
  {
    path: "/uppdrag/:projektId",
    element: <LegacyProjectRedirect />,
  },
  {
    path: "/profile",
    element: <Navigate to="/app/profile" replace />,
  },
  {
    path: "/admin",
    element: <Navigate to="/app/admin" replace />,
  },
  {
    path: "*",
    element: <MainLayout><NotFound /></MainLayout>,
  },
]);

// Legacy redirect component
import { useParams } from "react-router-dom";

function LegacyProjectRedirect() {
  const { projektId } = useParams();
  return <Navigate to={`/app/project/${projektId}`} replace />;
}
